import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtCurrency, ASSET_META } from "@/lib/format";
import { TrendChart, AllocationPie } from "@/components/charts";

const FIELDS = {
  crypto: [
    { key: "name", label: "Name", type: "text" },
    { key: "coin_id", label: "CoinGecko ID", type: "text", hint: "e.g. bitcoin" },
    { key: "quantity", label: "Quantity", type: "number" },
  ],
  stocks: [
    { key: "name", label: "Name", type: "text" },
    { key: "ticker", label: "Ticker", type: "text", hint: "e.g. AAPL, ASML.AS" },
    { key: "quantity", label: "Quantity", type: "number" },
  ],
  commodities: [
    { key: "name", label: "Name", type: "text" },
    { key: "ticker", label: "Ticker (optional)", type: "text", hint: "e.g. GC=F, GLD", optional: true },
    { key: "quantity", label: "Quantity", type: "number" },
    { key: "manual_price", label: "Manual price / unit", type: "number", optional: true },
  ],
  housing: [
    { key: "name", label: "Name", type: "text" },
    { key: "estimated_value", label: "Estimated value", type: "number" },
  ],
  cash: [
    { key: "name", label: "Name", type: "text" },
    { key: "balance", label: "Balance", type: "number" },
  ],
  other: [
    { key: "name", label: "Name", type: "text" },
    { key: "estimated_value", label: "Estimated value", type: "number" },
  ],
};

const inputCls =
  "w-full px-2.5 py-1.5 rounded-md border border-input bg-white/70 text-sm focus:outline-none focus:border-foreground/40 transition-colors duration-200";

export default function AssetCategory({ type }) {
  const meta = ASSET_META[type];
  const fields = FIELDS[type];

  const [holdings, setHoldings] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [h, snap] = await Promise.all([
        api.get("/assets/holdings", { params: { type } }),
        api.get("/assets/snapshots"),
      ]);
      setHoldings(h.data.holdings);
      setCurrency(h.data.base_currency);
      setSnapshots(snap.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [type]);

  useEffect(() => {
    setForm({});
    load();
  }, [load]);

  const parseVal = (f, raw) => (f.type === "number" ? (raw === "" ? null : parseFloat(raw)) : raw);

  const addHolding = async (e) => {
    e.preventDefault();
    const payload = { type };
    for (const f of fields) {
      const v = parseVal(f, form[f.key] ?? "");
      if (v === null || v === "" || v === undefined) {
        if (!f.optional) {
          toast.error(`${f.label} is required`);
          return;
        }
      } else {
        payload[f.key] = v;
      }
    }
    setAdding(true);
    try {
      await api.post("/assets/holdings", payload);
      setForm({});
      await load();
      toast.success("Holding added");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const saveField = async (id, f, raw) => {
    try {
      await api.put(`/assets/holdings/${id}`, { [f.key]: parseVal(f, raw) });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/assets/holdings/${id}`);
      setHoldings((h) => h.filter((x) => x.id !== id));
      toast.success("Holding removed");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const total = holdings.reduce((sum, h) => sum + (h.value ?? 0), 0);
  const composition = holdings
    .filter((h) => h.value != null && h.value > 0)
    .map((h, i) => ({ name: h.name, value: h.value, color: shade(meta.color, i) }));

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid={`asset-category-${type}`}>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{meta.label} total</p>
          <p className="font-serif text-3xl md:text-4xl text-foreground" data-testid="category-total">{fmtCurrency(total, currency)}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-serif text-xl text-foreground mb-4">{meta.label} over time</h2>
          <TrendChart snapshots={snapshots} getValue={(s) => s.by_type?.[type]} color={meta.color} currency={currency} testid="category-trend" />
        </div>
        <div className="rounded-xl border border-border bg-card p-6">
          <h2 className="font-serif text-xl text-foreground mb-4">Composition</h2>
          <AllocationPie data={composition} currency={currency} testid="category-composition" />
        </div>
      </div>

      {/* Add form */}
      <form onSubmit={addHolding} className="rounded-xl border border-border bg-card p-5" data-testid="holding-add-form">
        <div className="flex flex-wrap items-end gap-3">
          {fields.map((f) => (
            <div key={f.key} className="flex-1 min-w-[140px]">
              <label className="block text-xs text-muted-foreground mb-1">{f.label}</label>
              <input
                type={f.type === "number" ? "number" : "text"}
                step="any"
                className={inputCls}
                value={form[f.key] ?? ""}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.hint || ""}
                data-testid={`add-field-${f.key}`}
              />
            </div>
          ))}
          <button
            type="submit"
            disabled={adding}
            className="flex items-center gap-1.5 px-4 py-2 rounded-md text-background font-medium disabled:opacity-50"
            style={{ backgroundColor: meta.color, color: "#2C2C2C" }}
            data-testid="holding-add-submit"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </div>
      </form>

      {/* Holdings list */}
      <div className="rounded-xl border border-border bg-card p-5 overflow-x-auto">
        {holdings.length === 0 ? (
          <p className="text-sm text-muted-foreground italic" data-testid="category-empty">No holdings yet.</p>
        ) : (
          <table className="w-full text-sm" data-testid="category-holdings-table">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                {fields.map((f) => (
                  <th key={f.key} className="py-2 pr-3 font-medium">{f.label}</th>
                ))}
                <th className="py-2 pr-3 font-medium text-right">Value</th>
                <th className="py-2 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {holdings.map((h) => (
                <tr key={h.id} className="border-b border-border/60 last:border-0" data-testid={`holding-row-${h.id}`}>
                  {fields.map((f) => (
                    <td key={f.key} className="py-2 pr-3">
                      <input
                        type={f.type === "number" ? "number" : "text"}
                        step="any"
                        defaultValue={h[f.key] ?? ""}
                        onBlur={(e) => {
                          const raw = e.target.value;
                          const cur = h[f.key] ?? "";
                          if (String(raw) !== String(cur)) saveField(h.id, f, raw);
                        }}
                        className={inputCls}
                        data-testid={`holding-${h.id}-${f.key}`}
                      />
                    </td>
                  ))}
                  <td className="py-2 pr-3 text-right font-medium whitespace-nowrap">
                    {h.value == null ? <span className="text-muted-foreground italic">unavailable</span> : fmtCurrency(h.value, currency)}
                  </td>
                  <td className="py-2 text-right">
                    <button onClick={() => remove(h.id)} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`holding-delete-${h.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function shade(hex, i) {
  const palette = [hex, "#c9b28c", "#a9c0d0", "#c4b6d6", "#9fc7bf", "#dcb9bd", "#bcd2b4"];
  return i === 0 ? hex : palette[(i % (palette.length - 1)) + 1];
}
